import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileAudio, Sparkles, Wand2 } from 'lucide-react';
import { analyzeConversation } from '@/lib/ai';
import { cleanText } from '@/lib/ai-presentation';
import { updateProject } from '@/lib/api';
import { transcribeAudioAndWait } from '@/lib/transcription';
import type { AiTips, Project } from '@/lib/types';

interface Props {
  project: Project;
  clientId?: string | null;
  clientName?: string | null;
  packageBudget?: number;
  packageQuantity?: number;
  budgetType?: 'incl_btw' | 'excl_btw';
  onSaved: () => void;
}

export function ConversationStep({
  project,
  clientId,
  clientName,
  packageBudget,
  packageQuantity,
  budgetType,
  onSaved,
}: Props) {
  const [transcript, setTranscript] = useState(project.description ?? '');
  const [summary, setSummary] = useState(project.ai_summary ?? '');
  const [tips, setTips] = useState<AiTips>(project.ai_tips ?? {});
  const [busy, setBusy] = useState(false);
  const [audioStage, setAudioStage] = useState<'idle' | 'uploading' | 'transcribing'>('idle');
  const [audioDone, setAudioDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // De async transcriptie-flow duurt 10-60s; in die tijd kan de gebruiker
  // doortypen. Een ref houdt altijd de actuele textarea-inhoud vast zodat de
  // append niet op een verouderde closure-snapshot gebeurt (incident 11-06).
  const transcriptRef = useRef(transcript);
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  // Externe wijzigingen (andere save, reload na onSaved) terugspiegelen in het
  // tekstveld — zelfde patroon als TranscriptCard.
  useEffect(() => {
    setTranscript(project.description ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id, project.description]);

  async function handleAudioUpload(file: File) {
    setError(null);
    setAudioDone(null);
    const effectiveClientId = clientId ?? project.client_id;
    if (!effectiveClientId) {
      setError('Koppel eerst een klant aan dit project voordat je audio uploadt.');
      return;
    }
    try {
      const text = await transcribeAudioAndWait({
        file,
        clientId: effectiveClientId,
        onUploading: () => setAudioStage('uploading'),
        onTranscribing: () => setAudioStage('transcribing'),
      });
      const base = transcriptRef.current;
      const next = base ? `${base}\n\n--- Transcriptie ${file.name} ---\n${text}` : text;
      setTranscript(next);
      await updateProject(project.id, { description: next });
      setAudioDone(file.name);
      onSaved();
      // Transcript wordt onderaan toegevoegd; scroll mee zodat het resultaat
      // zichtbaar is in een al gevuld tekstveld.
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transcriptie mislukt');
    } finally {
      setAudioStage('idle');
    }
  }

  async function handleAnalyze() {
    if (!transcript.trim()) {
      setError('Voer eerst transcript-tekst in voordat je analyseert.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await analyzeConversation({
        transcript,
        client_name: clientName,
        budget: packageBudget,
        quantity: packageQuantity,
        budget_type: budgetType,
        deadline: project.deadline,
      });
      setSummary(result.summary);
      setTips(result.tips);
      await updateProject(project.id, {
        description: transcript,
        ai_summary: result.summary,
        ai_tips: result.tips,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'AI-analyse mislukt');
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveTranscript() {
    // Alleen schrijven bij echte wijziging — voorkomt dat een blur met
    // ongewijzigde (mogelijk verouderde) staat een nieuwere save overschrijft.
    if (transcript === (project.description ?? '')) return;
    await updateProject(project.id, { description: transcript });
    onSaved();
  }

  return (
    <div className="flex h-full flex-col gap-5">
      <header>
        <div className="text-xs uppercase tracking-wider text-bmade-500 dark:text-bmade-400">
          Stap 2: Gesprek met klant
        </div>
        <h2 className="mt-1 text-xl font-semibold text-bmade-950 dark:text-white">
          Plak de samenvatting of upload de opname
        </h2>
        <p className="text-sm text-bmade-600 dark:text-bmade-400">
          Klik "Analyseer met AI" voor een samenvatting, allergie-detectie, leverancier-tips en
          product-suggesties. Mark blijft de baas: niets wordt automatisch toegevoegd.
        </p>
      </header>

      <div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Linker: input */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
              Transcript / samenvatting
            </span>
            <label className="inline-flex items-center gap-1.5 cursor-pointer rounded-md border border-bmade-200 px-2 py-1 text-xs text-bmade-700 hover:border-accent-400 hover:text-accent-700 dark:border-bmade-700 dark:text-bmade-300">
              <FileAudio className="h-3 w-3" />
              {audioStage === 'uploading'
                ? 'Uploaden...'
                : audioStage === 'transcribing'
                  ? 'Whisper aan het werk...'
                  : 'Audio uploaden + transcriberen'}
              <input
                type="file"
                accept="audio/*"
                hidden
                disabled={audioStage !== 'idle'}
                onChange={(e) => e.target.files?.[0] && handleAudioUpload(e.target.files[0])}
              />
            </label>
          </div>
          <textarea
            ref={textareaRef}
            rows={14}
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            onBlur={handleSaveTranscript}
            placeholder="Plak hier de samenvatting van het inspiratie-gesprek of typ wat je weet over de klant en het pakket..."
            className="flex-1 rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm leading-relaxed text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white"
          />
          {audioStage === 'transcribing' && (
            <p className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-300">
              Whisper transcribeert. Dit kan tot 2 minuten duren afhankelijk van bestandsgrootte.
              Resultaat komt automatisch in het tekstveld.
            </p>
          )}
          {audioDone && audioStage === 'idle' && !error && (
            <p className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              Transcriptie van "{audioDone}" is onderaan het tekstveld toegevoegd en opgeslagen.
            </p>
          )}
          <button
            type="button"
            onClick={handleAnalyze}
            disabled={busy || !transcript.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-40"
          >
            <Wand2 className={`h-4 w-4 ${busy ? 'animate-pulse' : ''}`} />
            {busy ? 'AI denkt na...' : summary ? 'Analyseer opnieuw' : 'Analyseer met AI'}
          </button>
        </div>

        {/* Rechter: AI resultaat */}
        <div className="flex flex-col gap-3 overflow-auto rounded-xl border border-bmade-200 bg-bmade-50/40 p-4 dark:border-bmade-800 dark:bg-bmade-950/30">
          {summary || (tips.product_suggestions?.length ?? 0) > 0 ? (
            <>
              <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-accent-600 dark:text-accent-400">
                <Sparkles className="h-3 w-3" />
                AI-analyse
              </div>

              {summary && (
                <section>
                  <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
                    Samenvatting
                  </h3>
                  <p className="whitespace-pre-line text-sm leading-relaxed text-bmade-800 dark:text-bmade-200">
                    {cleanText(summary)}
                  </p>
                </section>
              )}

              {tips.dietary_warnings && tips.dietary_warnings.length > 0 && (
                <section>
                  <h3 className="mb-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-3 w-3" />
                    Allergie / dieet
                  </h3>
                  <div className="flex flex-wrap gap-1">
                    {tips.dietary_warnings.map((w, i) => (
                      <span
                        key={i}
                        className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-200"
                      >
                        {w}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {tips.supplier_tips && tips.supplier_tips.length > 0 && (
                <section>
                  <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
                    Leverancier-tips
                  </h3>
                  <ul className="space-y-1">
                    {tips.supplier_tips.map((t, i) => (
                      <li
                        key={i}
                        className="rounded border border-bmade-200 bg-white p-2 text-xs dark:border-bmade-800 dark:bg-bmade-900/40"
                      >
                        <span className="font-medium text-bmade-950 dark:text-white">
                          {t.supplier}
                        </span>{' '}
                        <span className="text-bmade-600 dark:text-bmade-400">{t.reason}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {tips.product_suggestions && tips.product_suggestions.length > 0 && (
                <section>
                  <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
                    Product-suggesties
                  </h3>
                  <ul className="space-y-1">
                    {tips.product_suggestions.map((p, i) => (
                      <li
                        key={i}
                        className="rounded border border-bmade-200 bg-white p-2 text-xs dark:border-bmade-800 dark:bg-bmade-900/40"
                      >
                        <div className="font-medium text-bmade-950 dark:text-white">{p.name}</div>
                        {p.supplier && (
                          <div className="text-[10px] text-bmade-500">{p.supplier}</div>
                        )}
                        <div className="mt-0.5 text-bmade-600 dark:text-bmade-400">{p.reason}</div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center text-center text-sm text-bmade-500">
              <Sparkles className="mb-2 h-6 w-6 text-accent-400" />
              <p>Klik "Analyseer met AI" zodra je transcript-tekst hebt.</p>
              <p className="mt-1 text-xs">
                Mark krijgt een samenvatting, tags en suggesties als hulp bij samenstellen.
              </p>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}
    </div>
  );
}
